import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { formatDate, formatINR } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { SelectField } from '../components/ui/Field';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  WithdrawalMethodChip,
  WithdrawalReviewDrawer,
  WithdrawalStatusChip,
  academicYearOptions,
  type WithdrawalRequest,
} from '../components/wallet';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  withdrawalsFilterConfig,
  withdrawalsSearchSchema,
  WITHDRAWALS_SORT_COLUMNS,
} from '../components/finance/withdrawalsFilterConfig';

interface ListResponseBody<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 25;

export const Route = createFileRoute('/_authed/finance/withdrawals')({
  validateSearch: withdrawalsSearchSchema,
  component: WithdrawalsQueuePage,
});

function WithdrawalsQueuePage() {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const userId = useAuthStore((s) => s.user?.id);
  const isFinance = role === 'finance_admin' || role === 'super_admin';
  const navigate = useNavigate();

  const yearOptions = useMemo(() => academicYearOptions(), []);
  const [selected, setSelected] = useState<WithdrawalRequest | null>(null);

  // The single source of truth for filters / sort / page, backed by the route's
  // URL search params. The adapter narrows the route's precisely-typed
  // search/navigate surface to the toolkit's structural `FilterRoute` contract;
  // the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(withdrawalsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  // `academicYear` stays a sibling single-select (the backend filters it with a
  // single `eq`, so it can't be a comma-joined toolkit facet). It lives in the
  // same URL search contract (`withdrawalsSearchSchema`), so read/write it there
  // — via the route's own typed `useSearch`/`useNavigate` — and fold it into the
  // one query alongside `apiParams`.
  const yearFilter = Route.useSearch().academicYear ?? '';
  const routeNavigate = Route.useNavigate();
  const setYearFilter = (next: string) => {
    void routeNavigate({
      search: (prev) => ({
        ...prev,
        academicYear: next || undefined,
        // Changing a filter resets pagination — mirror the toolkit's behaviour.
        page: undefined,
      }),
    });
  };

  // ONE query, driven by the toolkit's `apiParams` (the default status
  // `['requested','approved']` reproduces the old "open" view in a single call)
  // plus the sibling year select. The previous requested + approved two-query
  // merge is gone — the backend now serves the open view directly.
  const listQuery = useQuery({
    queryKey: ['wallet', 'withdrawals', 'queue', apiParams, yearFilter] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponseBody<WithdrawalRequest>>('/api/wallet/withdrawals', {
        signal,
        query: {
          ...apiParams,
          ...(yearFilter ? { academicYear: yearFilter } : {}),
        },
      }),
    enabled: isFinance,
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  if (!isFinance) {
    return (
      <ListOrOutlet basePath="/finance/withdrawals">
      <ListShell title="Withdrawals" description="Finance admin queue.">
        <EmptyState
          title="This page is for finance admins"
          description="Use your dashboard for your role."
        />
      </ListShell>
      </ListOrOutlet>
    );
  }

  return (
    <ListOrOutlet basePath="/finance/withdrawals">
    <ListShell
      title="Withdrawals"
      description="Review and pay out wallet withdrawal requests."
    >
      <FilterBar config={withdrawalsFilterConfig} filters={filters} resultCount={total} />

      <div className="mb-4 max-w-xs">
        <SelectField
          label="Academic year"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          options={[
            { value: '', label: 'All years' },
            ...yearOptions.map((y) => ({ value: y, label: y })),
          ]}
        />
      </div>

      <GatedSection
        error={listQuery.error}
        title="Withdrawals"
        description="Once the wallet & referrals module is enabled, payout requests will appear here for review."
        onRetry={() => void listQuery.refetch()}
      >
        {isLoadingList ? (
          <Skeleton className="h-72 w-full" />
        ) : items.length === 0 ? (
          <EmptyState
            title="No withdrawals match these filters"
            description="Try clearing a filter. When teachers or students request withdrawals you'll see them here."
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[color:var(--color-surface)]">
                    <tr>
                      <Th>Requester</Th>
                      <Th>Year</Th>
                      <Th align="right">Amount</Th>
                      <Th>Method</Th>
                      <Th>Status</Th>
                      <Th>
                        <SortableHeader
                          label="Requested"
                          binding={WITHDRAWALS_SORT_COLUMNS.requestedAt}
                          currentSort={values.sort}
                          onSort={setSort}
                        />
                      </Th>
                      <Th>Reviewed</Th>
                      <Th>Paid</Th>
                      <Th>
                        <span className="sr-only">Edit</span>
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((req) => (
                      <tr
                        key={req.id}
                        onClick={() =>
                          void navigate({
                            to: '/finance/withdrawals/$withdrawalId',
                            params: { withdrawalId: req.id },
                          })
                        }
                        className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
                      >
                        <td className="px-4 py-2 align-middle">
                          <p className="font-medium text-[color:var(--color-text)]">
                            {req.ownerType === 'teacher' ? 'Teacher' : 'Student'}
                          </p>
                          <p className="font-mono text-xs text-[color:var(--color-text-muted)]">
                            {req.ownerRefId.slice(0, 8)}…
                          </p>
                        </td>
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                          {req.academicYear}
                        </td>
                        <td className="px-4 py-2 align-middle text-right tabular-nums font-semibold">
                          {formatINR(req.requestedAmountPaise)}
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <WithdrawalMethodChip method={req.method} />
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <WithdrawalStatusChip status={req.status} />
                        </td>
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                          {formatDate(req.requestedAt)}
                        </td>
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                          {req.reviewedAt ? formatDate(req.reviewedAt) : '—'}
                        </td>
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                          {req.paidAt ? formatDate(req.paidAt) : '—'}
                        </td>
                        <td className="px-2 py-2 align-middle text-right">
                          <EditIconButton
                            onClick={() => setSelected(req)}
                            label="Review withdrawal"
                            title="Review withdrawal"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination
              page={values.page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </GatedSection>

      <WithdrawalReviewDrawer
        request={selected}
        currentUserId={userId}
        currentUserRole={role}
        onClose={() => setSelected(null)}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}
